import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import mixpanel from "mixpanel-browser";

import { SignupPage } from "./screens/SignupPage";
import { LoginPage } from "./screens/LoginPage";
import { VerifyEmailPage } from "./screens/VerifyEmailPage";
import { ForgotPasswordPage } from "./screens/ForgotPasswordPage";
import { ResetPasswordPage } from "./screens/ResetPasswordPage";
import { AuthCallback } from "./components/AuthCallback";
import { TestCallback } from "./components/TestCallback";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { MIXPANEL_TOKEN } from "../src/api";
import { initSentry } from "./config/sentry";

// Heavy or rarely-visited routes get split into their own chunk. The signup
// and login flows stay eager so the cold-start LCP for the most common
// entrypoints does not pay a network round trip for a chunk fetch.
const Dashboard = lazy(() =>
  import("./screens/Dashboard").then((m) => ({ default: m.Dashboard })),
);
const SetupWizard = lazy(() =>
  import("./screens/SetupWizard").then((m) => ({ default: m.SetupWizard })),
);
const TermsOfService = lazy(() =>
  import("./screens/TermsOfService").then((m) => ({
    default: m.TermsOfService,
  })),
);
const PrivacyPolicy = lazy(() =>
  import("./screens/PrivacyPolicy").then((m) => ({ default: m.PrivacyPolicy })),
);
const RefundPolicy = lazy(() =>
  import("./screens/RefundPolicy").then((m) => ({ default: m.RefundPolicy })),
);

initSentry();

if (MIXPANEL_TOKEN) {
  mixpanel.init(MIXPANEL_TOKEN, { autocapture: true });
} else {
  console.warn("Mixpanel token is not configured");
}

const RouteFallback = () => (
  <div
    className="min-h-screen flex items-center justify-center bg-gray-50 px-4"
    role="status"
    aria-live="polite"
    aria-label="Loading page"
  >
    <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-900 animate-spin" />
  </div>
);

createRoot(document.getElementById("app") as HTMLElement).render(
  <StrictMode>
    <BrowserRouter>
      <main id="app-main">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<SignupPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/refund" element={<RefundPolicy />} />
            <Route path="/auth/google/callback" element={<AuthCallback />} />
            <Route path="/test" element={<TestCallback />} />
            <Route path="/oauth/test" element={<div>OAUTH TEST WORKS</div>} />
            <Route path="/oauth/callback" element={<AuthCallback />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/setup"
              element={
                <ProtectedRoute>
                  <SetupWizard />
                </ProtectedRoute>
              }
            />
            <Route
              path="*"
              element={
                <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                  <div className="max-w-md text-center">
                    <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                      Page not found
                    </h1>
                    <p className="text-gray-600">
                      The page you tried to open does not exist.
                    </p>
                  </div>
                </div>
              }
            />
          </Routes>
        </Suspense>
      </main>
    </BrowserRouter>
  </StrictMode>,
);
